import { Fragment, type ReactNode } from 'react';
import { parseServerContent } from '../../../components/composer/mentionUtils';
import { SLASH_COMMANDS } from '../../../components/composer/slashCommands';
import { MentionChip } from './MentionChip';
import { CommandChip } from './CommandChip';

// A message from a slash command starts with its trigger (e.g.
// "/salarybenchmark …"). Peel that off so it renders as a highlighted chip,
// matching how it looked in the composer. Only known commands qualify — a plain
// "/foo" the user typed stays text.
const leadingCommand = (content: string): string | null => {
  const match = content.match(/^(\/[a-z]+)(?=\s|$)/i);
  const cmd = match?.[1].toLowerCase();
  return cmd && SLASH_COMMANDS.some((c) => c.cmd === cmd) ? cmd : null;
};

// Walk parsed tokens into React children. Text segments keep their
// whitespace (the surrounding <Text> uses `whitespace-pre-wrap`), mention
// tokens become atomic chips, and a leading slash command becomes its own chip.
export const renderTokens = (content: string): ReactNode => {
  const command = leadingCommand(content);
  const body = command ? content.slice(command.length) : content;

  const tokens = parseServerContent(body);
  const rendered =
    tokens.length === 0
      ? body
      : tokens.map((token, idx) => {
          if (token.type === 'text') {
            return <Fragment key={idx}>{token.text}</Fragment>;
          }
          return (
            <MentionChip
              key={idx}
              name={token.mention.name}
              entity={token.mention.entity}
            />
          );
        });

  if (!command) return rendered;
  return (
    <>
      <CommandChip cmd={command} />
      {rendered}
    </>
  );
};
